import DOMPurify from "dompurify";
import { Scale, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

export const ProcessDialog = ({
  target, onClose, outlets, models, selected, setSelected, model, setModel,
  instructions, setInstructions, progress, busy, compareModels, process,
}) => (
  <Dialog open={!!target} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="process-dialog">
      <DialogHeader>
        <DialogTitle className="serif text-2xl">Generar borrador editorial</DialogTitle>
      </DialogHeader>
      <div className="space-y-4">
        <div>
          <Label className="text-xs uppercase tracking-wider">Medios de publicación</Label>
          <div className="space-y-2 mt-2">
            {(outlets || []).map((o) => (
              <label key={o.id} className="flex items-center gap-3 text-sm cursor-pointer">
                <Checkbox
                  checked={selected.includes(o.id)}
                  onCheckedChange={(c) =>
                    setSelected(c ? [...selected, o.id] : selected.filter((x) => x !== o.id))
                  }
                  data-testid={`outlet-checkbox-${o.id}`}
                />
                {o.name}
              </label>
            ))}
          </div>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Modelo de IA</Label>
          <select
            className="w-full mt-2 h-10 border border-input bg-background px-3 text-sm rounded-md"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            data-testid="model-select"
          >
            {(models?.models || []).map((m) => (
              <option key={m.id} value={m.id}>{m.label}</option>
            ))}
          </select>
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wider">Instrucciones para el redactor</Label>
          <Textarea
            rows={3}
            className="mt-2"
            placeholder="Ej.: enfoque en el impacto para los afiliados, incluir contexto normativo."
            value={instructions}
            onChange={(e) => setInstructions(e.target.value)}
            data-testid="instructions-input"
          />
        </div>
      </div>
      {progress && (
        <p className="text-xs mono text-muted-foreground" data-testid="ai-progress">
          {progress} · puede tardar hasta dos minutos, no cierre esta ventana
        </p>
      )}
      <DialogFooter className="flex-col sm:flex-row gap-2">
        <Button variant="outline" onClick={compareModels} disabled={busy === "compare"}
                data-testid="compare-models-btn">
          <Scale className="h-4 w-4 mr-2" />
          {busy === "compare" ? "Comparando…" : "Comparar con otro modelo"}
        </Button>
        <Button onClick={process} disabled={busy === "process"} data-testid="confirm-process-btn">
          <Sparkles className="h-4 w-4 mr-2" />
          {busy === "process" ? "Redactando…" : "Redactar noticia"}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

export const ComparisonDialog = ({ comparison, onClose, busy, applyDraft }) => (
  <Dialog open={!!comparison} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto" data-testid="comparison-dialog">
      <DialogHeader>
        <DialogTitle className="serif text-2xl">
          Comparar modelos · {comparison?.outlet}
        </DialogTitle>
      </DialogHeader>
      <div className="grid md:grid-cols-2 gap-5">
        {(comparison?.drafts || []).map((d) => (
          <div key={d.draft_id} className="border border-border p-4 flex flex-col"
               data-testid={`draft-${d.model}`}>
            <div className="flex items-center justify-between gap-2 mb-2">
              <span className="kicker text-primary">{d.label}</span>
              <span className="text-xs mono text-muted-foreground">
                {d.words} palabras · {d.seconds}s
              </span>
            </div>
            <p className="serif text-xl font-bold leading-tight">{d.title}</p>
            {d.subtitle && (
              <p className="serif italic text-muted-foreground mt-1">{d.subtitle}</p>
            )}
            <div className="prose prose-sm max-w-none mt-3 max-h-[46vh] overflow-y-auto"
                 dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(d.body_html || "") }} />
            {(d.pending_checks || []).length > 0 && (
              <ul className="text-xs text-muted-foreground mt-3 space-y-1">
                {d.pending_checks.slice(0, 3).map((c) => <li key={c}>· {c}</li>)}
              </ul>
            )}
            <Button className="mt-4" onClick={() => applyDraft(d.draft_id)}
                    disabled={busy === `use-${d.draft_id}`}
                    data-testid={`use-draft-${d.model}`}>
              {busy === `use-${d.draft_id}` ? "Creando…" : "Usar esta versión"}
            </Button>
          </div>
        ))}
      </div>
    </DialogContent>
  </Dialog>
);
